import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Gift, Copy, Share2, Users, Sparkles } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';

const ReferralPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/api/referral/me');
      setInfo(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const shareMessage = info
    ? `Book a professional driver for your car with BookMyDriver! Use my code ${info.referral_code} and I will get 25% off my next ride once you complete your first booking. Sign up: ${window.location.origin}`
    : '';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'BookMyDriver', text: shareMessage });
        return;
      } catch { /* user cancelled or unsupported, fall through to copy */ }
    }
    navigator.clipboard.writeText(shareMessage);
    toast({ title: 'Copied to clipboard', description: 'Share it with your friends!' });
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(info.referral_code);
    toast({ title: 'Code copied', description: info.referral_code });
  };

  const handleApply = async () => {
    if (!codeInput.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post('/api/referral/apply', { code: codeInput.trim() });
      setInfo(data);
      setCodeInput('');
      toast({ title: 'Referral code applied', description: "Your friend will get 25% off once you complete your first ride." });
    } catch (err) {
      toast({ title: 'Could not apply code', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  };

  const handleUsePendingDiscount = async () => {
    await refreshUser();
    navigate('/user/dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/user/dashboard')} data-testid="referral-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <BrandLogo variant="compact" className="h-8 w-auto" />
          <h1 className="text-xl font-bold border-l pl-4">Refer &amp; Earn</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        <Card className="p-6 bg-gradient-to-br from-primary to-primary-dark text-white text-center">
          <Gift className="w-12 h-12 mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Give a Referral, Get 25% Off</h2>
          <p className="text-white/90 text-sm">
            Share your code. When your friend completes their first booking, you get 25% off your next ride.
          </p>
        </Card>

        {info?.pending_discount_percent > 0 && (
          <Card className="p-6 border-2 border-green-300 bg-green-50 text-center" data-testid="pending-discount-card">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <p className="font-semibold text-green-800 mb-3">
              You have a {info.pending_discount_percent}% discount ready for your next booking!
            </p>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleUsePendingDiscount} data-testid="use-discount-btn">
              Book Now &amp; Save
            </Button>
          </Card>
        )}

        <Card className="p-6">
          <Label className="mb-2 block">Your Referral Code</Label>
          <div className="flex gap-2">
            <Input value={info?.referral_code || ''} readOnly className="h-12 font-mono text-lg font-bold text-center"
              data-testid="referral-code-value" />
            <Button variant="outline" size="icon" className="h-12 w-12" onClick={handleCopyCode} data-testid="copy-code-btn">
              <Copy className="w-5 h-5" />
            </Button>
          </div>
          <Button className="w-full mt-3 bg-primary hover:bg-primary-dark" onClick={handleShare} data-testid="share-referral-btn">
            <Share2 className="w-4 h-4 mr-2" />Share with Friends
          </Button>
          <p className="text-sm text-muted-foreground flex items-center gap-2 mt-4">
            <Users className="w-4 h-4" />{info?.referred_signups ?? 0} friend(s) have signed up with your code
          </p>
        </Card>

        {!info?.referral_applied && (
          <Card className="p-6">
            <h3 className="font-semibold mb-2">Have a friend's code?</h3>
            <p className="text-sm text-muted-foreground mb-3">Enter it before your first booking, your friend earns their reward once you complete it.</p>
            <div className="flex gap-2">
              <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="e.g. AB12CD" className="h-11 font-mono" data-testid="apply-referral-input" />
              <Button onClick={handleApply} disabled={applying} data-testid="apply-referral-btn">
                {applying ? 'Applying…' : 'Apply'}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
